// lib
import { useContext, useState, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

import { DataContext as SidebarDataContext } from '@aytm/app/stats/page/sidebar/Sidebar'

import { DataContext } from '@aytm/app/stats/page/sidebar/filters/FiltersPanel'
import { convertTreeToValues } from '@aytm/app/stats/lib/util/converter'

// components
import FiltersGroupQuestions from '@aytm/app/stats/page/sidebar/filters/groups/FiltersGroupQuestions'
import FiltersGroupCustomVariables from '@aytm/app/stats/page/sidebar/filters/groups/FiltersGroupCustomVariables'

import FiltersRowVariable from '@aytm/app/stats/page/sidebar/filters/rows/FiltersRowVariable'

import SidebarPopup from '@aytm/app/stats/page/sidebar/components/SidebarPopup'
import { pressable } from '@aytm/app/stats/components/lib/pressable'

import TransitionPanel from '@aytm/app/stats/components/transition-panel/TransitionPanel'

// styles
import style from '@aytm/app/stats/page/sidebar/filters/styles/FiltersPanel.module.scss'

const FiltersGroup = ({ group }) => {
  const { sidebarState, sidebarDispatch } = useContext(SidebarDataContext)

  const { root, state } = useContext(DataContext)

  const [hidden, setHidden] = useState(
    Object.hasOwn(sidebarState, 'filters') && Object.hasOwn(sidebarState.filters || {}, group.key)
      ? sidebarState.filters[group.key].hidden
      : group.hidden || false
  )
  const [collapsed, setCollapsed] = useState(
    Object.hasOwn(sidebarState, 'filters') && Object.hasOwn(sidebarState.filters || {}, group.key)
      ? sidebarState.filters[group.key].collapsed
      : true
  )

  const filtersCount = useMemo(() => {
    if ('advanced' == state.mode || null === state.basic) return 0

    return group.values.reduce((acc, cur) => {
      if (
        !cur.disabled &&
        state.basic[cur.key.toUpperCase()] != undefined &&
        0 < Object.keys(state.basic[cur.key.toUpperCase()]).length
      ) {
        return acc + 1
      }

      return acc
    }, 0)
  }, [group.values, state.mode, state.basic])

  const getFilterValues = useCallback(
    filter => {
      const value = filter.disabled
        ? null
        : null !== state.basic &&
            state.basic[filter.key.toUpperCase()] != undefined &&
            0 < Object.keys(state.basic[filter.key.toUpperCase()]).length
          ? convertTreeToValues(filter, state.basic[filter.key.toUpperCase()], 'value')
          : 'sliderselect' == filter.type
            ? [1, filter.options.length]
            : null

      return null !== value ? JSON.stringify(value) : value
    },
    [state.basic]
  )

  const getFilterOptions = useCallback(
    filter => {
      if (filter.parent) {
        const vals = filter.parent.map(
          v => state.basic && state.basic[filter.key.toUpperCase()] && state.basic[filter.key.toUpperCase()][v]
        )
        const keys = filter.parent.reduce((acc, cur, i) => {
          const value =
            vals[i] != undefined && null !== vals[i] ? (Array.isArray(vals[i].v) ? vals[i].v[0] : vals[i].v) : null
          const parent = group.values.find(v => v.key == filter.key && v.subkey == cur)
          const options = Array.isArray(parent.options)
            ? ['multistatemultiselect', 'multiselect'].includes(parent.type) && parent.group
              ? parent.options.flatMap(option => option.items)
              : parent.options
            : parent.options[acc.join('-')]
          const item = (options != undefined && options.find(opt => opt?.value == value)) || null
          acc.push(null !== item ? item.position : null)
          return acc
        }, [])
        return filter.options[keys.join('-')] || []
      }
      return filter.options
    },
    [group.values, state.basic]
  )

  useEffect(
    () =>
      sidebarDispatch({
        type: 'UPDATE_STATE',
        key: 'filters',
        state: {
          [group.key]: { collapsed, hidden }
        }
      }),
    [group.key, collapsed, hidden, sidebarDispatch]
  )

  useEffect(
    () =>
      setCollapsed(
        Object.hasOwn(sidebarState, 'filters') && Object.hasOwn(sidebarState.filters || {}, group.key)
          ? sidebarState.filters[group.key].collapsed
          : true
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [group.key, sidebarState.filters]
  )

  return 0 < group.values.length || 'customVariables' == group.key ? (
    hidden ? (
      <div
        data-testid={`stat-filters-group-${group.key}-switch`}
        className={style['filter-switch']}
        {...pressable(() => setHidden(false))}
      >
        Show {group.title.toLowerCase()}
      </div>
    ) : (
      <div
        data-testid={`stat-filters-group-${group.key}`}
        data-filters-count={filtersCount}
        data-collapsed={collapsed}
        className={style['filter-group']}
      >
        <div
          id={`FiltersPanel-group-${group.key}`}
          className={classNames(style['filter-header'], { [style['filter-header-active']]: 0 < filtersCount })}
        >
          <div
            className={style['filter-subheader']}
            aria-expanded={!collapsed}
            {...pressable(() => setCollapsed(!collapsed))}
          >
            <span
              data-testid={`stat-filters-group-${group.key}-toggle`}
              className={classNames(style['filter-toggle'], { [style['filter-toggle-collapsed']]: collapsed })}
            >
              <i className="icon-aytmfd-chevron-bracket" />
            </span>
            <span data-testid={`stat-filters-group-${group.key}-title`} className={style['filter-title']}>
              {group.title}
            </span>
            {0 < filtersCount ? (
              <span data-testid={`stat-filters-group-${group.key}-counter`} className={style['filter-count']}>
                {filtersCount}
              </span>
            ) : null}
          </div>
          {group.help && root ? (
            <SidebarPopup root={root} help={group.help} width={300 < group.help.length ? '600px' : '300px'} />
          ) : null}
        </div>
        <TransitionPanel
          data-testid={`stat-filters-group-${group.key}-container`}
          className={style['filter-container']}
          in={!collapsed}
        >
          {status =>
            'exited' === status ? null : ['virtualQuestions', 'questions', 'excludedQuestions'].includes(group.key) ? (
              <FiltersGroupQuestions
                group={group}
                getFilterValues={getFilterValues}
                getFilterOptions={getFilterOptions}
              />
            ) : 'customVariables' == group.key ? (
              <FiltersGroupCustomVariables
                group={group}
                opened={'entered' === status}
                getFilterValues={getFilterValues}
                getFilterOptions={getFilterOptions}
              />
            ) : (
              group.values.map(filter => (
                <FiltersRowVariable
                  key={filter.subkey ? `${filter.key}${filter.subkey}` : filter.key}
                  filter={filter}
                  filterValues={getFilterValues(filter)}
                  filterOptions={getFilterOptions(filter)}
                />
              ))
            )
          }
        </TransitionPanel>
      </div>
    )
  ) : null
}

FiltersGroup.propTypes = {
  group: PropTypes.object
}

FiltersGroup.displayName = 'FiltersGroup'

export default FiltersGroup
